import { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Divider, MenuItem, Paper, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  grnApi, purchaseInvoicesApi,
  type CreatePurchaseInvoiceRequest,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { todayIso } from "@/shared/datetime";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

interface GrnDetail {
  id: string; documentNo: string; vendorName: string; purchaseOrderNo: string;
  lines: GrnLine[];
}
interface GrnLine {
  id: string; itemId: string; itemName: string; sku: string;
  batchNo?: string; expiryDate?: string;
  receivedQty: number; acceptedQty: number; unitCost: number;
}

interface DraftLine {
  grnLineId: string; itemId: string; itemName: string; sku: string;
  acceptedQty: number;
  qty: number; rate: number; taxAmount: number; discountAmount: number;
}

/**
 * UC-M1-PROC-05 — create a Purchase Invoice from a posted GRN. Three-way match is enforced
 * server-side (PI qty ≤ GRN acceptedQty); the form pre-fills qty = acceptedQty and rate =
 * unitCost from the GRN so the common path is one tap, with header GST + discount editable.
 */
export function CreatePurchaseInvoicePage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();

  const [grnId, setGrnId] = useState<string>(params.get("grnId") ?? "");
  const [documentDate, setDocumentDate] = useState<string>(todayIso);
  const [vendorInvoiceNo, setVendorInvoiceNo] = useState<string>("");
  const [vendorInvoiceDate, setVendorInvoiceDate] = useState<string>("");
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  const [notes, setNotes] = useState<string>("");
  const [lines, setLines] = useState<DraftLine[]>([]);

  // Postable GRNs available for invoicing (status=Posted).
  const grnList = useQuery({
    queryKey: ["grns-postable"],
    queryFn: () => grnApi.list({ status: "Posted", pageSize: 100 }) as Promise<{ items: { id: string; documentNo: string; vendorName: string }[] }>,
  });

  const grnDetail = useQuery<GrnDetail>({
    queryKey: ["grn-detail", grnId],
    queryFn: () => grnApi.get(grnId) as Promise<GrnDetail>,
    enabled: !!grnId,
  });

  // Auto-fill lines whenever a GRN is selected.
  useEffect(() => {
    if (!grnDetail.data) return;
    setLines(grnDetail.data.lines.map(gl => ({
      grnLineId: gl.id, itemId: gl.itemId, itemName: gl.itemName, sku: gl.sku,
      acceptedQty: gl.acceptedQty,
      qty: gl.acceptedQty, rate: gl.unitCost, taxAmount: 0, discountAmount: 0,
    })));
  }, [grnDetail.data]);

  const updateLine = (idx: number, patch: Partial<DraftLine>) =>
    setLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));

  const subTotal = lines.reduce((s, l) => s + l.qty * l.rate, 0);
  const taxTotal = lines.reduce((s, l) => s + l.taxAmount, 0);
  const lineDisc = lines.reduce((s, l) => s + l.discountAmount, 0);
  const total = subTotal + taxTotal - lineDisc - discountAmount;

  const overInvoicedLines = lines.filter(l => l.qty > l.acceptedQty);
  const hasErrors = overInvoicedLines.length > 0 || !grnId || lines.length === 0;

  const mutation = useMutation({
    meta: { successMessage: "Purchase invoice created" },
    mutationFn: async () => {
      const cmd: CreatePurchaseInvoiceRequest = {
        grnId, documentDate,
        vendorInvoiceNo: vendorInvoiceNo || undefined,
        vendorInvoiceDate: vendorInvoiceDate || undefined,
        discountAmount, notes: notes || undefined,
        lines: lines.map(l => ({
          grnLineId: l.grnLineId, itemId: l.itemId,
          qty: l.qty, rate: l.rate, taxAmount: l.taxAmount, discountAmount: l.discountAmount,
        })),
      };
      return await purchaseInvoicesApi.create(cmd);
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["purchase-invoices"] });
      navigate(`/procurement/purchase-invoices/${r.id}`);
    },
  });

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New purchase invoice</Typography>
      <Paper sx={{ p: 3 }}>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}

        <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
          <TextField select label="Posted GRN *" size="small" sx={{ minWidth: 320 }}
            value={grnId} onChange={(e) => setGrnId(e.target.value)}>
            <MenuItem value="">— pick a GRN —</MenuItem>
            {grnList.data?.items.map(g => (
              <MenuItem key={g.id} value={g.id}>{g.documentNo} — {g.vendorName}</MenuItem>
            ))}
          </TextField>
          <TextField label="PI date *" type="date" size="small"
            InputLabelProps={{ shrink: true }}
            value={documentDate} onChange={(e) => setDocumentDate(e.target.value)} />
          <TextField label="Vendor invoice #" size="small"
            value={vendorInvoiceNo} onChange={(e) => setVendorInvoiceNo(e.target.value)} />
          <TextField label="Vendor invoice date" type="date" size="small"
            InputLabelProps={{ shrink: true }}
            value={vendorInvoiceDate} onChange={(e) => setVendorInvoiceDate(e.target.value)} />
          <TextField label="Header discount (₹)" type="number" size="small" sx={{ width: 180 }}
            value={discountAmount}
            onChange={(e) => setDiscountAmount(Number(e.target.value) || 0)}
            inputProps={{ min: 0, step: "0.01" }} />
        </Stack>

        {grnDetail.data && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Source: GRN <b>{grnDetail.data.documentNo}</b> · Vendor <b>{grnDetail.data.vendorName}</b> ·
            Linked PO <b>{grnDetail.data.purchaseOrderNo}</b>. PI qty cannot exceed GRN accepted qty.
          </Alert>
        )}
        {overInvoicedLines.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {overInvoicedLines.length} line(s) exceed the GRN accepted qty. Lower the invoiced qty
            or have the vendor issue a corrected invoice.
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Item</TableCell>
              <TableCell align="right">GRN accepted</TableCell>
              <TableCell align="right">Invoiced qty *</TableCell>
              <TableCell align="right">Rate (₹) *</TableCell>
              <TableCell align="right">Tax (₹)</TableCell>
              <TableCell align="right">Discount (₹)</TableCell>
              <TableCell align="right">Line total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((l, idx) => {
              const lineTotal = l.qty * l.rate + l.taxAmount - l.discountAmount;
              const over = l.qty > l.acceptedQty;
              return (
                <TableRow key={l.grnLineId} sx={over ? { bgcolor: "error.lighter" } : undefined}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{l.itemName}</Typography>
                    <Typography variant="caption" color="text.secondary">{l.sku}</Typography>
                  </TableCell>
                  <TableCell align="right">{l.acceptedQty}</TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" sx={{ width: 100 }}
                      value={l.qty}
                      onChange={(e) => updateLine(idx, { qty: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0, step: "0.001", style: { textAlign: "right" } }}
                      error={over}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" sx={{ width: 110 }}
                      value={l.rate}
                      onChange={(e) => updateLine(idx, { rate: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" sx={{ width: 100 }}
                      value={l.taxAmount}
                      onChange={(e) => updateLine(idx, { taxAmount: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" sx={{ width: 100 }}
                      value={l.discountAmount}
                      onChange={(e) => updateLine(idx, { discountAmount: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }} />
                  </TableCell>
                  <TableCell align="right">{inr.format(lineTotal)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Divider sx={{ my: 3 }} />
        <TextField label="Notes" size="small" fullWidth multiline minRows={2}
          value={notes} onChange={(e) => setNotes(e.target.value)} sx={{ mb: 2 }} />

        <Stack direction="row" justifyContent="flex-end" spacing={4} sx={{ mb: 2 }}>
          <Stack><Typography variant="caption" color="text.secondary">Subtotal</Typography>
            <Typography>{inr.format(subTotal)}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Tax</Typography>
            <Typography>{inr.format(taxTotal)}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Discount</Typography>
            <Typography>− {inr.format(lineDisc + discountAmount)}</Typography></Stack>
          <Stack><Typography variant="caption" color="text.secondary">Total</Typography>
            <Typography variant="h6" color="primary">{inr.format(total)}</Typography></Stack>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button onClick={() => navigate("/procurement/purchase-invoices")}>Cancel</Button>
          <Button variant="contained" color="success" disabled={hasErrors || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
            onClick={() => mutation.mutate()}>
            Save draft PI
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
